import {
  debounceTime,
  defer,
  distinctUntilChanged,
  filter,
  interval,
  map,
  NEVER,
  Observable,
  of,
  race,
  scan,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
  timer,
  withLatestFrom,
} from 'rxjs';
import { currentTimestamp } from '@ardoq/date-time';
import { ViewIds, type ArdoqId } from '@ardoq/api-types';
import { toError, type NonEmptyArray } from '@ardoq/common-helpers';
import { activeScenarioOperations } from '@ardoq/core';
import {
  captureExport,
  EXPORT_FORMAT,
  exportGraphComponent,
  exportHtmlToPng,
  getExportProfilingTransaction,
  type CaptureOutcome,
} from '@ardoq/export';
import type { I18nLocale } from '@ardoq/i18n';
import { logWarn } from '@ardoq/logging';
import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
  type Action,
} from '@ardoq/rxbeach';
import { updateViewContext, type ViewContext } from '@ardoq/view-settings';
import {
  toViewpointModeMainViewInstanceId,
  toWorkspaceModeMainViewInstanceId,
} from 'appLayout/mainViewInstanceIds';
import {
  asyncNoop,
  viewModifiers$,
} from 'appLayout/ardoqStudio/viewPaneHeader/viewModifiers$';
import { metamodelNavigation$ } from 'metamodel/navigation/metamodelNavigation$';
import { toMetamodelExportConfig } from 'metamodel/metamodelExportConfig';
import { selectSlide } from 'presentation/slideNavigator/actions';
import {
  slideCaptureSurface$,
  type HtmlSlideSurface,
  type MetamodelSlideSurface,
} from 'presentation/viewPane/slideCaptureSurface$';
import { slideIsLoading$ } from 'presentation/viewPane/slideIsLoading$';
import { viewPane$ } from 'presentation/viewPane/viewPane$';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { MAIN_PANE_LEFT } from 'streams/views/mainContent/types';
import { isUpdatingAppState$ } from 'isUpdatingAppState$';
import { getStyleSheets } from 'tabview/getSharedExportFunctions';
import {
  notifyViewLoading,
  notifyViewRenderDone,
  type NotifyViewLoadingPayload,
} from 'tabview/actions';
import { activeViewsStatus$ } from 'tabview/activeViewsStatus/activeViewsStatus$';
import {
  getViewHolds,
  isViewRegistered,
} from 'tabview/activeViewsStatus/activeViewsStatusOperations';
import { whenViewSettled$ } from 'tabview/activeViewsStatus/viewStatusReaders';
import type { ActiveViewsStatus } from 'tabview/activeViewsStatus/types';
import { getExportLabels } from 'views/getExportLabels';
import type {
  CaptureTarget,
  SlideContent,
  SlideImage,
  SlideToCapture,
} from './slidePlan';
import {
  isViewContextOf,
  NOT_READY,
  recordViewLoading,
  toFinalHtmlSlideReadiness,
  toFinalMetamodelReadiness,
  toFinalScenarioSlideReadiness,
  toFinalSlideReadiness,
  toFinalSurfaceReadiness,
  toHtmlSlideReadiness,
  toMetamodelReadiness,
  toScenarioSlideReadiness,
  toScenarioViewContext,
  toSlideReadiness,
  toSurfaceReadiness,
  toViewBusyState,
  type HtmlSlideSnapshot,
  type HtmlSlideToCapture,
  type MetamodelSlideToCapture,
  type MetamodelSnapshot,
  type ScenarioSlideToCapture,
  type ScenarioViewerSnapshot,
  type SlideReadiness,
  type SlideSurface,
  type ViewBusyByNamespace,
  type ViewBusyState,
  type ViewerSnapshot,
  type VisualizationSlideToCapture,
} from './slideReadiness';
import { IMAGE_BOX_PX } from './buildDeck';
import { toContext, toSliceImages } from './slideImageSlices';
import {
  htmlTableLayout$,
  toImageTableLayout,
  toTableSliceImages,
  type TableLayout,
} from './tableLayout';
import { fromAbortable, whileRecording } from './subscriptionScopes';

const POLL_MS = 50;
const READY_TIMEOUT_MS = 30000;
const QUIET_MS = 500;
const SETTLE_CAP_MS = 3000;
const SETTLED_CAP_MS = 15000;
const IDLE_STABLE_MS = 150;
const UNSEEN_GRACE_MS = 1000;
const IDLE_CAP_MS = 15000;
export const CAPTURE_TIMEOUT_MS = 60000;
export const CAPTURE_SETTLE_MS = 5000;
const MAX_IMAGE_AREA = 4096 * 4096;
const FINAL_READ_CAP_MS = 1000;

const pollTick$ = interval(POLL_MS).pipe(startWith(0));

const viewerSnapshot$: Observable<ViewerSnapshot> = pollTick$.pipe(
  withLatestFrom(viewPane$, slideIsLoading$, viewModifiers$),
  map(([, { loadedSlide, slideError }, isLoading, modifiers]) => ({
    loadedSlideId: loadedSlide?._id ?? null,
    hasSlideError: Boolean(slideError),
    isLoading,
    mainViewId: modifiers.mainViewId,
    viewPropertiesViewId: modifiers.viewPropertiesViewId,
    exportToPng:
      modifiers.exportToPng === asyncNoop ? null : modifiers.exportToPng,
    isContainerConnected: Boolean(
      modifiers.viewContext.getContainer()?.isConnected
    ),
  }))
);

const exportScenarioViewToPng = (
  viewContext: ViewContext,
  view: ViewIds,
  i18nLocale: I18nLocale
): Promise<void> =>
  exportGraphComponent({
    container: viewContext.getContainer,
    graphComponent: viewContext.getGraphComponent,
    format: EXPORT_FORMAT.PNG,
    exportedViewMetadata: { name: view, isViewpointMode: false },
    getStyleSheets,
    labels: getExportLabels(i18nLocale),
  });

type ScenarioViewerSnapshotSources = {
  reportedViewContext$: Observable<ViewContext | null>;
  viewInstanceId: string;
  view: ViewIds;
  i18nLocale: I18nLocale;
};

const scenarioViewerSnapshot$ = ({
  reportedViewContext$,
  viewInstanceId,
  view,
  i18nLocale,
}: ScenarioViewerSnapshotSources): Observable<ScenarioViewerSnapshot> =>
  pollTick$.pipe(
    withLatestFrom(
      viewPane$,
      slideIsLoading$,
      viewModifiers$,
      activeScenario$,
      isUpdatingAppState$,
      reportedViewContext$
    ),
    map(
      ([
        ,
        { loadedSlide, slideError },
        isLoading,
        modifiers,
        activeScenario,
        isUpdatingAppState,
        reportedViewContext,
      ]) => {
        const viewContext = toScenarioViewContext({
          reported: reportedViewContext,
          modifiersContext: modifiers.viewContext,
          viewInstanceId,
        });
        return {
          loadedSlideId: loadedSlide?._id ?? null,
          hasSlideError: Boolean(slideError),
          isLoading,
          mainViewId: modifiers.mainViewId,
          exportToPng: viewContext?.getGraphComponent()
            ? () => exportScenarioViewToPng(viewContext, view, i18nLocale)
            : null,
          isContainerConnected: Boolean(
            viewContext?.getContainer()?.isConnected
          ),
          activeScenarioId:
            activeScenarioOperations.getActiveScenarioId(activeScenario),
          isInDiffMode: activeScenarioOperations.isInDiffMode(activeScenario),
          isUpdatingAppState,
        };
      }
    )
  );

type ReadinessProbe = {
  readiness$: Observable<SlideReadiness>;
  finalReadiness$: Observable<SlideReadiness>;
};

const toReadinessProbe = <Snapshot>(
  snapshot$: Observable<Snapshot>,
  toReadiness: (snapshot: Snapshot) => SlideReadiness,
  toFinalReadiness: (snapshot: Snapshot) => SlideReadiness
): ReadinessProbe => ({
  readiness$: snapshot$.pipe(map(toReadiness)),
  finalReadiness$: snapshot$.pipe(
    take(1),
    map(toFinalReadiness),
    timeout({ first: FINAL_READ_CAP_MS, with: () => of(NOT_READY) })
  ),
});

const firstReadiness$ = ({
  readiness$,
  finalReadiness$,
}: ReadinessProbe): Observable<SlideReadiness> =>
  readiness$.pipe(
    filter(readiness => readiness.status !== 'waiting'),
    take(1),
    timeout({ first: READY_TIMEOUT_MS, with: () => finalReadiness$ })
  );

const domQuiet$ = (): Observable<void> =>
  new Observable<void>(subscriber => {
    const observer = new MutationObserver(() => subscriber.next());
    observer.observe(document.getElementById('root') ?? document.body, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }).pipe(startWith(undefined), debounceTime(QUIET_MS));

const domQuietOrCapped$ = (): Observable<unknown> =>
  race(domQuiet$(), timer(SETTLE_CAP_MS)).pipe(take(1));

const logUnsettledView = (
  status: ActiveViewsStatus,
  viewInstanceId: string
): void => {
  const now = currentTimestamp();
  const holds = getViewHolds(status, viewInstanceId);
  logWarn(Error('Slide view did not settle before capture'), null, {
    viewInstanceId,
    holds: Object.entries(holds).map(([kind, { since }]) => ({
      kind,
      ageMs: now - since,
    })),
  });
};

export const viewSettledOrCapped$ = (
  viewInstanceId: string
): Observable<unknown> =>
  whenViewSettled$(viewInstanceId).pipe(
    timeout({
      first: SETTLED_CAP_MS,
      with: () =>
        activeViewsStatus$.pipe(
          take(1),
          tap(status => logUnsettledView(status, viewInstanceId))
        ),
    })
  );

const recordViewLoadingAction = (
  busyByNamespace: ViewBusyByNamespace,
  { meta: { namespace }, payload: { isBusy } }: Action<NotifyViewLoadingPayload>
): ViewBusyByNamespace =>
  namespace
    ? recordViewLoading(busyByNamespace, { namespace, isBusy })
    : busyByNamespace;

const IDLE_DELAY_MS: Record<Exclude<ViewBusyState, 'busy'>, number> = {
  idle: IDLE_STABLE_MS,
  unseen: UNSEEN_GRACE_MS,
};

const toIdleTimer = (state: ViewBusyState): Observable<unknown> =>
  state === 'busy' ? NEVER : timer(IDLE_DELAY_MS[state]);

const viewIdle$ = (
  busyByNamespace$: Observable<ViewBusyByNamespace>,
  viewInstanceId: string
): Observable<unknown> =>
  busyByNamespace$.pipe(
    map(busyByNamespace => toViewBusyState(busyByNamespace, viewInstanceId)),
    distinctUntilChanged(),
    switchMap(toIdleTimer),
    take(1),
    timeout({ first: IDLE_CAP_MS, with: () => of(undefined) })
  );

const notifyViewLoadingSettled$ = (
  viewInstanceId: string,
  renderDone$: Observable<unknown>,
  busyByNamespace$: Observable<ViewBusyByNamespace>
): Observable<unknown> =>
  race(renderDone$, domQuiet$(), timer(SETTLE_CAP_MS)).pipe(
    take(1),
    switchMap(() => viewIdle$(busyByNamespace$, viewInstanceId))
  );

const settled$ = (
  viewInstanceId: string,
  renderDone$: Observable<unknown>,
  busyByNamespace$: Observable<ViewBusyByNamespace>
): Observable<unknown> =>
  activeViewsStatus$.pipe(
    take(1),
    switchMap(status =>
      isViewRegistered(status, viewInstanceId)
        ? viewSettledOrCapped$(viewInstanceId)
        : notifyViewLoadingSettled$(
            viewInstanceId,
            renderDone$,
            busyByNamespace$
          )
    )
  );

const nextAnimationFrame = (): Promise<void> =>
  new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

const waitForPaint = async (): Promise<void> => {
  await document.fonts.ready;
  await nextAnimationFrame();
  await nextAnimationFrame();
};

const confirmedAfterPaint$ = (
  finalReadiness$: Observable<SlideReadiness>
): Observable<SlideReadiness> =>
  defer(waitForPaint).pipe(
    switchMap(() => finalReadiness$),
    map(latest => (latest.status === 'ready' ? latest : NOT_READY))
  );

type ProbeReadyWait = {
  probe: ReadinessProbe;
  settled$: Observable<unknown>;
};

const probeReady$ = ({
  probe,
  settled$,
}: ProbeReadyWait): Observable<SlideReadiness> =>
  firstReadiness$(probe).pipe(
    switchMap(readiness =>
      readiness.status === 'ready'
        ? settled$.pipe(
            take(1),
            switchMap(() => confirmedAfterPaint$(probe.finalReadiness$))
          )
        : of(readiness)
    )
  );

type SurfaceReadyWait = ProbeReadyWait & { slideId: ArdoqId };

const surfaceReady$ = ({
  slideId,
  ...wait
}: SurfaceReadyWait): Observable<SlideReadiness> =>
  defer(() => {
    dispatchAction(selectSlide(slideId));
    return probeReady$(wait);
  });

export const readerSurfaceReady$ = (
  surface$: Observable<SlideSurface>
): Observable<SlideReadiness> =>
  probeReady$({
    probe: toReadinessProbe(
      surface$,
      toSurfaceReadiness,
      toFinalSurfaceReadiness
    ),
    settled$: domQuietOrCapped$(),
  });

type SlideReadyWait = {
  slide: VisualizationSlideToCapture;
  viewInstanceId: string;
  probe: ReadinessProbe;
};

const slideReady$ = ({
  slide,
  viewInstanceId,
  probe,
}: SlideReadyWait): Observable<SlideReadiness> =>
  whileRecording(
    action$.pipe(
      ofType(notifyViewRenderDone),
      extractPayload(),
      filter(viewId => viewId === slide.view)
    ),
    renderDone$ =>
      whileRecording(
        action$.pipe(
          ofType(notifyViewLoading),
          scan(recordViewLoadingAction, {}),
          startWith({})
        ),
        busyByNamespace$ =>
          surfaceReady$({
            slideId: slide.id,
            probe,
            settled$: settled$(viewInstanceId, renderDone$, busyByNamespace$),
          })
      )
  );

const viewpointSlideReady$ = (
  slide: VisualizationSlideToCapture
): Observable<SlideReadiness> =>
  slideReady$({
    slide,
    viewInstanceId: toViewpointModeMainViewInstanceId(slide.view),
    probe: toReadinessProbe(
      viewerSnapshot$,
      snapshot => toSlideReadiness(snapshot, slide),
      snapshot => toFinalSlideReadiness(snapshot, slide)
    ),
  });

type ScenarioSlideCapture = {
  slide: ScenarioSlideToCapture;
  i18nLocale: I18nLocale;
};

const scenarioSlideReady$ = ({
  slide,
  i18nLocale,
}: ScenarioSlideCapture): Observable<SlideReadiness> => {
  const viewInstanceId = toWorkspaceModeMainViewInstanceId(
    MAIN_PANE_LEFT,
    slide.view
  );
  return whileRecording(
    action$.pipe(
      ofType(updateViewContext),
      extractPayload(),
      filter(viewContext => isViewContextOf(viewContext, viewInstanceId)),
      startWith(null)
    ),
    reportedViewContext$ =>
      slideReady$({
        slide,
        viewInstanceId,
        probe: toReadinessProbe(
          scenarioViewerSnapshot$({
            reportedViewContext$,
            viewInstanceId,
            view: slide.view,
            i18nLocale,
          }),
          snapshot => toScenarioSlideReadiness(snapshot, slide),
          snapshot => toFinalScenarioSlideReadiness(snapshot, slide)
        ),
      })
  );
};

type SurfaceCapture = Pick<
  SlideSurface,
  'exportToPng' | 'isContainerConnected'
>;

type MetamodelCaptureArgs = {
  surface: MetamodelSlideSurface;
  name: string;
  i18nLocale: I18nLocale;
};

export const toMetamodelCapture = ({
  surface: { graph, container },
  name,
  i18nLocale,
}: MetamodelCaptureArgs): SurfaceCapture => ({
  exportToPng:
    graph && container
      ? () =>
          exportGraphComponent({
            container,
            graphComponent: graph.graphComponent,
            format: EXPORT_FORMAT.PNG,
            ...toMetamodelExportConfig(name, i18nLocale),
          })
      : null,
  isContainerConnected: container?.isConnected ?? false,
});

const metamodelSnapshot$ = (
  i18nLocale: I18nLocale
): Observable<MetamodelSnapshot> =>
  pollTick$.pipe(
    withLatestFrom(viewPane$, metamodelNavigation$, slideCaptureSurface$),
    map(
      ([
        ,
        { loadedSlide, slideError },
        { metamodel },
        { metamodel: surface },
      ]) => ({
        loadedSlideId: loadedSlide?._id ?? null,
        hasSlideError: Boolean(slideError),
        metamodelId: metamodel?._id ?? null,
        graphMetamodelId: surface.graph?.metamodelId ?? null,
        ...toMetamodelCapture({
          surface,
          name: metamodel?.name ?? '',
          i18nLocale,
        }),
      })
    )
  );

type MetamodelSlideCapture = {
  slide: MetamodelSlideToCapture;
  i18nLocale: I18nLocale;
};

const metamodelSlideReady$ = ({
  slide,
  i18nLocale,
}: MetamodelSlideCapture): Observable<SlideReadiness> =>
  surfaceReady$({
    slideId: slide.id,
    probe: toReadinessProbe(
      metamodelSnapshot$(i18nLocale),
      snapshot => toMetamodelReadiness(snapshot, slide),
      snapshot => toFinalMetamodelReadiness(snapshot, slide)
    ),
    settled$: of(undefined),
  });

const exportHtmlSlideToPng = (
  container: HTMLElement,
  i18nLocale: I18nLocale
): Promise<void> =>
  exportHtmlToPng({
    container,
    transaction: getExportProfilingTransaction(),
    exportedViewMetadata: { name: ViewIds.NONE, isViewpointMode: false },
    getStyleSheets,
    labels: getExportLabels(i18nLocale),
  });

export const toHtmlCapture = (
  html: HtmlSlideSurface | null,
  i18nLocale: I18nLocale
): SurfaceCapture => ({
  exportToPng: html
    ? () => exportHtmlSlideToPng(html.container, i18nLocale)
    : null,
  isContainerConnected: html?.container.isConnected ?? false,
});

const htmlSlideSnapshot$ = (
  i18nLocale: I18nLocale
): Observable<HtmlSlideSnapshot> =>
  pollTick$.pipe(
    withLatestFrom(viewPane$, slideCaptureSurface$),
    map(([, { loadedSlide, slideError }, { html }]) => ({
      loadedSlideId: loadedSlide?._id ?? null,
      hasSlideError: Boolean(slideError),
      surfaceSlideId: html?.slideId ?? null,
      isSurfaceBusy: html?.isBusy ?? true,
      hasSurfaceError: html?.hasError ?? false,
      ...toHtmlCapture(html, i18nLocale),
    }))
  );

type HtmlSlideWait = {
  slide: HtmlSlideToCapture;
  i18nLocale: I18nLocale;
};

const htmlSlideReady$ = ({
  slide,
  i18nLocale,
}: HtmlSlideWait): Observable<SlideReadiness> =>
  surfaceReady$({
    slideId: slide.id,
    probe: toReadinessProbe(
      htmlSlideSnapshot$(i18nLocale),
      snapshot => toHtmlSlideReadiness(snapshot, slide),
      snapshot => toFinalHtmlSlideReadiness(snapshot, slide)
    ),
    settled$: domQuietOrCapped$(),
  });

type ImageSize = { w: number; h: number };

export const toSlideImageSize = (width: number, height: number): ImageSize => {
  const scale = Math.min(1, Math.sqrt(MAX_IMAGE_AREA / (width * height)));
  return {
    w: Math.max(1, Math.round(width * scale)),
    h: Math.max(1, Math.round(height * scale)),
  };
};

export const toSlideImages = async (
  blob: Blob,
  tableLayout: TableLayout | null
): Promise<NonEmptyArray<SlideImage>> => {
  const bitmap = await createImageBitmap(blob);
  const { w, h } = toSlideImageSize(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const context = toContext(canvas);
  context.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return tableLayout
    ? toTableSliceImages(context, {
        layout: toImageTableLayout(tableLayout, h),
        box: IMAGE_BOX_PX,
      })
    : toSliceImages(context, IMAGE_BOX_PX);
};

const logCaptureFailure = (error: unknown, target: CaptureTarget): void =>
  logWarn(toError(error), 'Slide capture for the PPTX export failed', {
    target,
  });

type ImageCapture = {
  target: CaptureTarget;
  tableLayout: TableLayout | null;
};

const toImageContent = async (
  blob: Blob,
  { target, tableLayout }: ImageCapture
): Promise<SlideContent> => {
  try {
    return { kind: 'image', images: await toSlideImages(blob, tableLayout) };
  } catch (error) {
    logCaptureFailure(error, target);
    return { kind: 'placeholder', reason: 'failed' };
  }
};

type CaptureSlideArgs = {
  slide: SlideToCapture;
  i18nLocale: I18nLocale;
};

const targetReady$ = ({
  slide: { id, target },
  i18nLocale,
}: CaptureSlideArgs): Observable<SlideReadiness> => {
  switch (target.type) {
    case 'metamodel':
      return metamodelSlideReady$({
        slide: { id, metamodelId: target.metamodelId },
        i18nLocale,
      });
    case 'html':
      return htmlSlideReady$({ slide: { id }, i18nLocale });
    case 'visualization':
      return viewpointSlideReady$({ id, view: target.view });
    case 'scenario':
      return scenarioSlideReady$({
        slide: {
          id,
          view: target.view,
          scenarioId: target.scenarioId,
          isInVisualDiffMode: target.isInVisualDiffMode,
        },
        i18nLocale,
      });
  }
};

const captureOutcome$ = (
  exportToPng: () => Promise<void>
): Observable<CaptureOutcome> =>
  fromAbortable(signal =>
    captureExport(exportToPng, {
      timeoutMs: CAPTURE_TIMEOUT_MS,
      settleMs: CAPTURE_SETTLE_MS,
      signal,
    })
  );

const toOutcomeContent = (
  outcome: CaptureOutcome,
  capture: ImageCapture
): Observable<SlideContent> => {
  const { target } = capture;
  switch (outcome.kind) {
    case 'blob':
      return defer(() => toImageContent(outcome.blob, capture));
    case 'aborted':
      return of({ kind: 'placeholder', reason: 'failed' });
    case 'no-delivery':
      return of({ kind: 'placeholder', reason: 'no-image' });
    case 'failed':
    case 'too-large':
      logCaptureFailure(Error(outcome.message), target);
      return of({ kind: 'placeholder', reason: outcome.kind });
    case 'timeout':
    case 'busy':
      return of({ kind: 'placeholder', reason: outcome.kind });
  }
};

const tableLayoutOf$ = (
  target: CaptureTarget
): Observable<TableLayout | null> =>
  target.type === 'html' ? htmlTableLayout$ : of(null);

/**
 * Selects the slide, waits for it to be ready and captures it. Unsubscribing
 * cancels the wait and aborts a capture in flight.
 */
export const captureSlide$ = (
  args: CaptureSlideArgs
): Observable<SlideContent> =>
  targetReady$(args).pipe(
    switchMap(readiness => {
      if (readiness.status === 'ready') {
        const { target } = args.slide;
        return tableLayoutOf$(target).pipe(
          switchMap(tableLayout =>
            captureOutcome$(readiness.exportToPng).pipe(
              switchMap(outcome =>
                toOutcomeContent(outcome, { target, tableLayout })
              )
            )
          )
        );
      }
      const reason =
        readiness.status === 'unready' ? readiness.reason : 'not-ready';
      return of<SlideContent>({ kind: 'placeholder', reason });
    })
  );
